<template>
    <v-container class="mt-3 pb-12 dashboard-container"
        style="min-width: 1200px; max-width: 1600px; padding-left: 24px; padding-right: 24px;">
        <v-row>
            <v-col cols="12">
                <!-- My Work header -->
                <div class="d-flex align-center justify-space-between mb-1 my-work-header">
                    <div>
                        <h1 class="text-h5 font-weight-bold mb-0">My Work</h1>
                        <p class="text-caption text--secondary mb-0">View and manage your tasks, assignments and task requests.</p>
                    </div>

                    <div class="my-work-header-actions">
                        <!-- Admin User Selector -->
                        <v-autocomplete v-model="selectedViewingUser" :items="users()" item-text="name"
                            item-value="id" placeholder="View tasks for user"
                            prepend-inner-icon="mdi-account-search" clearable dense hide-details
                            solo flat class="filter-search-style"
                            style="max-width: 280px;"
                            data-tour="user-selector"
                            :disabled="isPersonalTasksLoading || isAssignedTasksLoading"
                            :loading="isPersonalTasksLoading || isAssignedTasksLoading"
                            @change="onUserSelectionChange"></v-autocomplete>

                        <!-- Search (routes into whichever tab's filters is active) -->
                        <v-text-field v-model="activeSearchQuery" placeholder="Search tasks..."
                            prepend-inner-icon="mdi-magnify" dense hide-details solo flat clearable
                            class="filter-search-style" style="max-width: 220px;" />

                        <!-- Bulk Selection Info -->
                        <v-chip v-if="selected_tasks.length > 0" color="primary" outlined small>
                            {{ selected_tasks.length }} task{{ selected_tasks.length == 1 ? '' : 's' }} selected
                            <v-btn @click="openDeleteBulkTasksDialog()" color="error" icon x-small class="ml-2"
                                :disabled="isPersonalTasksLoading || isAssignedTasksLoading">
                                <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                        </v-chip>

                        <!-- Refresh -->
                        <v-btn fab small depressed outlined color="grey darken-1" class="icon-action-btn"
                            :loading="refreshing" @click="refreshData">
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>

                        <!-- Action Buttons -->
                        <v-btn-toggle rounded class="create-task-group elevation-0">
                            <v-btn @click="openNewTaskDialog('self')" color="success" depressed class="text-none"
                                data-tour="create-task-btn"
                                :disabled="isPersonalTasksLoading || isAssignedTasksLoading"
                                :loading="isPersonalTasksLoading">
                                <v-icon left>mdi-plus</v-icon>
                                Create Task
                            </v-btn>
                            <v-menu offset-y>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-bind="attrs" v-on="on" color="success" depressed
                                        data-tour="assign-task-btn"
                                        :disabled="isPersonalTasksLoading || isAssignedTasksLoading"
                                        :loading="isAssignedTasksLoading">
                                        <v-icon>mdi-menu-down</v-icon>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item @click="openNewTaskDialog('assign')">
                                        <v-list-item-icon>
                                            <v-icon>mdi-account-plus</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>Assign Task to Someone</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </v-btn-toggle>

                        <!-- Voice-to-Task Button -->
                        <v-btn
                            fab small depressed
                            :color="isRecording ? 'error' : 'deep-purple accent-2'"
                            :class="['icon-action-btn', isRecording ? 'voice-btn-recording' : '']"
                            @click="toggleVoiceRecording"
                            :disabled="isProcessingVoice"
                        >
                            <v-icon color="white">
                                {{ isRecording ? 'mdi-stop' : 'mdi-microphone' }}
                            </v-icon>
                        </v-btn>
                    </div>
                </div>

                <!-- Work Tabs: one row combining list/calendar view with task scope
                     (sits on the page background, not the white card below it).
                     Only 5 of the 6 possible list/calendar × scope combinations are
                     real — Pending Requests has no calendar view since nothing wires
                     pending-approval tasks into ScheduleView, and previously
                     Calendar+Pending Requests silently duplicated Calendar+Assigned
                     by Me instead of showing anything distinct. Dropped rather than
                     fixed, since a calendar of not-yet-approved tasks isn't
                     meaningful (they don't have a schedule yet). -->
                <v-tabs v-model="activeWorkTab" color="primary" height="40" class="view-mode-toggle"
                    data-tour="view-mode-toggle" :disabled="isPersonalTasksLoading && isAssignedTasksLoading">
                    <v-tab class="px-4" data-tour="personal-tasks-tab">
                        <v-icon left small>mdi-format-list-checks</v-icon>
                        My Task List
                    </v-tab>
                    <v-tab class="px-4" data-tour="schedule-mode-btn">
                        <v-icon left small>mdi-calendar-clock</v-icon>
                        My Schedule View
                    </v-tab>
                    <v-tab class="px-4" data-tour="assigned-tasks-tab">
                        <v-icon left small>mdi-account-group</v-icon>
                        Assigned Task List
                    </v-tab>
                    <v-tab class="px-4">
                        <v-icon left small>mdi-calendar-account</v-icon>
                        Assigned Schedule View
                    </v-tab>
                    <v-tab class="px-4" data-tour="created-for-others-tab">
                        <v-icon left small>mdi-account-arrow-right</v-icon>
                        Pending Task Requests
                    </v-tab>
                </v-tabs>

                <v-card elevation="0">
                    <v-card-text class="pb-0 pt-2">
                        <!-- Voice Processing Status Banner -->
                        <v-expand-transition>
                            <div v-if="isProcessingVoice || isRecording" class="mb-3 mt-1">
                                <v-alert
                                    dense
                                    text
                                    :color="isRecording ? 'error' : 'primary'"
                                    class="mb-0 py-2"
                                    style="border-radius: 10px;"
                                >
                                    <div class="d-flex align-center" style="gap: 10px;">
                                        <v-progress-circular
                                            v-if="isProcessingVoice"
                                            indeterminate
                                            size="18"
                                            width="2"
                                            :color="isRecording ? 'error' : 'primary'"
                                        />
                                        <v-icon v-if="isRecording" color="error" small class="voice-pulse">mdi-microphone</v-icon>
                                        <span style="font-size: 0.85rem; font-weight: 500;">
                                            <span v-if="isRecording">Listening... Speak your task clearly, then click Stop</span>
                                            <span v-else-if="voiceStatus">{{ voiceStatus }}</span>
                                        </span>
                                    </div>
                                </v-alert>
                            </div>
                        </v-expand-transition>

                        <!-- Compact stats strip -->
                        <v-sheet outlined rounded class="mb-3 stats-strip position-relative">
                            <v-overlay :value="isLoadingMyTaskStats()" absolute>
                                <v-progress-circular indeterminate size="28" color="primary" />
                            </v-overlay>
                            <div class="stats-strip-items">
                                <div class="stats-strip-item">
                                    <span class="stats-strip-icon stats-strip-icon-blue">
                                        <v-icon color="blue" size="18">mdi-format-list-bulleted</v-icon>
                                    </span>
                                    <div class="stats-strip-text">
                                        <span class="stats-strip-value">{{ liveStats.todo }}</span>
                                        <span class="stats-strip-label">To Do</span>
                                    </div>
                                </div>
                                <v-divider vertical class="stats-strip-divider"></v-divider>
                                <div class="stats-strip-item">
                                    <span class="stats-strip-icon stats-strip-icon-orange">
                                        <v-icon color="orange" size="18">mdi-progress-clock</v-icon>
                                    </span>
                                    <div class="stats-strip-text">
                                        <span class="stats-strip-value">{{ liveStats.inProgress }}</span>
                                        <span class="stats-strip-label">In Progress</span>
                                    </div>
                                </div>
                                <v-divider vertical class="stats-strip-divider"></v-divider>
                                <div class="stats-strip-item">
                                    <span class="stats-strip-icon stats-strip-icon-error">
                                        <v-icon color="error" size="18">mdi-alert-circle</v-icon>
                                    </span>
                                    <div class="stats-strip-text">
                                        <span class="stats-strip-value">{{ liveStats.overdue }}</span>
                                        <span class="stats-strip-label">Overdue</span>
                                    </div>
                                </div>
                                <v-divider vertical class="stats-strip-divider"></v-divider>
                                <div class="stats-strip-item">
                                    <span class="stats-strip-icon stats-strip-icon-success">
                                        <v-icon color="success" size="18">mdi-check-circle</v-icon>
                                    </span>
                                    <div class="stats-strip-text">
                                        <span class="stats-strip-value">{{ liveStats.completed }}</span>
                                        <span class="stats-strip-label">Completed</span>
                                    </div>
                                </div>
                            </div>
                        </v-sheet>

                        <!-- Filters (List mode only — Calendar tabs don't consume them) -->
                        <div v-if="viewMode === 'tasks'" class="mb-4 filters-row">
                            <TaskFilterToolbar v-if="activeTab === 0"
                                :status="personalStatusFilters" @update:status="personalStatusFilters = $event"
                                :status-options="statusFilterOptions"
                                :company="personalFilters.company" @update:company="personalFilters.company = $event"
                                :company-options="companyFilterOptions"
                                :priority="personalFilters.priority" @update:priority="personalFilters.priority = $event"
                                :priority-options="priorityFilterOptions"
                                show-assignee :assignee="personalFilters.assignee" @update:assignee="personalFilters.assignee = $event"
                                :assignee-options="assigneeFilterOptions"
                                :due-date="personalFilters.dueDate" @update:due-date="personalFilters.dueDate = $event"
                                :due-date-options="dueDateFilters"
                                show-group-by :group-by="personalGroupBy" @update:group-by="personalGroupBy = $event"
                                :group-by-options="groupByOptions"
                                :sort-by="personalSortBy" @update:sort-by="personalSortBy = $event"
                                :sort-by-options="sortByOptions"
                                :disabled="isPersonalTasksLoading"
                                @clear="clearPersonalFilters"
                            />
                            <TaskFilterToolbar v-else-if="activeTab === 1"
                                :status="assignedStatusFilters" @update:status="assignedStatusFilters = $event"
                                :status-options="statusFilterOptions"
                                :company="assignedFilters.company" @update:company="assignedFilters.company = $event"
                                :company-options="companyFilterOptions"
                                :priority="assignedFilters.priority" @update:priority="assignedFilters.priority = $event"
                                :priority-options="priorityFilterOptions"
                                show-assignee :assignee="assignedFilters.assignee" @update:assignee="assignedFilters.assignee = $event"
                                :assignee-options="assigneeFilterOptions"
                                :due-date="assignedFilters.dueDate" @update:due-date="assignedFilters.dueDate = $event"
                                :due-date-options="dueDateFilters"
                                show-group-by :group-by="assignedGroupBy" @update:group-by="assignedGroupBy = $event"
                                :group-by-options="groupByOptions"
                                :sort-by="assignedSortBy" @update:sort-by="assignedSortBy = $event"
                                :sort-by-options="sortByOptions"
                                :disabled="isAssignedTasksLoading"
                                @clear="clearAssignedFilters"
                            />
                            <TaskFilterToolbar v-else
                                :show-status="false"
                                :company="createdForOthersFilters.company" @update:company="createdForOthersFilters.company = $event"
                                :company-options="companyFilterOptions"
                                :priority="createdForOthersFilters.priority" @update:priority="createdForOthersFilters.priority = $event"
                                :priority-options="priorityFilterOptions"
                                :due-date="createdForOthersFilters.dueDate" @update:due-date="createdForOthersFilters.dueDate = $event"
                                :due-date-options="dueDateFilters"
                                show-group-by :group-by="createdForOthersGroupBy" @update:group-by="createdForOthersGroupBy = $event"
                                :group-by-options="groupByOptionsNoStatus"
                                :sort-by="createdForOthersSortBy" @update:sort-by="createdForOthersSortBy = $event"
                                :sort-by-options="sortByOptions"
                                :disabled="isPersonalTasksLoading"
                                @clear="clearCreatedForOthersFilters"
                            />
                        </div>

                        <!-- Schedule View -->
                        <ScheduleView v-if="viewMode === 'schedule'"
                            :task-type="activeTab === 0 ? 'personal' : 'assigned'" />

                        <!-- Task List View -->
                        <v-tabs-items v-else v-model="activeTab">
                            <!-- My Task List Tab -->
                            <v-tab-item>
                                <div class="quick-task-bar mb-3">
                                    <v-btn-toggle v-model="headerQuickTaskMode" mandatory dense class="quick-task-toggle">
                                        <v-btn value="single" x-small>
                                            <v-icon small>mdi-file-plus</v-icon>
                                        </v-btn>
                                        <v-btn value="multiple" x-small>
                                            <v-icon small>mdi-file-multiple</v-icon>
                                        </v-btn>
                                    </v-btn-toggle>
                                    <v-text-field v-if="headerQuickTaskMode === 'single'" v-model="headerQuickTaskName"
                                        placeholder="Type a task name and press Enter..."
                                        dense solo flat hide-details class="filter-search-style quick-task-bar-input"
                                        data-tour="quick-task-input"
                                        :loading="headerQuickTaskCreating" :disabled="headerQuickTaskCreating"
                                        @keydown.enter="submitHeaderQuickTask">
                                        <template v-slot:prepend-inner>
                                            <v-icon small color="grey">mdi-plus</v-icon>
                                        </template>
                                    </v-text-field>
                                    <v-textarea v-else v-model="headerQuickTaskNamesText"
                                        placeholder="Enter task names, one per line..."
                                        dense solo flat rows="3" hide-details class="filter-search-style quick-task-bar-input"
                                        data-tour="quick-task-input" :disabled="headerQuickTaskCreating">
                                        <template v-slot:prepend-inner>
                                            <v-icon small color="grey">mdi-plus</v-icon>
                                        </template>
                                    </v-textarea>
                                    <v-autocomplete v-model="headerQuickTaskCompanyId" :items="[{ name: 'No Company', id: null }, ...companies()]"
                                        item-text="name" item-value="id" dense solo flat hide-details clearable
                                        placeholder="Select company (optional)" class="filter-search-style quick-task-bar-company"
                                        data-tour="quick-task-company">
                                        <template v-slot:prepend-inner>
                                            <v-icon small color="grey">mdi-domain</v-icon>
                                        </template>
                                    </v-autocomplete>

                                    <v-expand-transition>
                                        <div v-if="headerQuickTaskMode === 'multiple' && headerQuickTaskNamesList.length > 0" class="quick-task-queue">
                                            <v-chip-group column>
                                                <v-chip v-for="(name, index) in headerQuickTaskNamesList" :key="index" x-small outlined close
                                                    @click:close="removeHeaderQuickTaskName(index)">
                                                    {{ name }}
                                                </v-chip>
                                            </v-chip-group>
                                            <v-btn small color="primary" :loading="headerQuickTaskCreating" @click="createHeaderQuickTasks">
                                                <v-icon small left>mdi-plus</v-icon>
                                                Create {{ headerQuickTaskNamesList.length }} Tasks
                                            </v-btn>
                                        </div>
                                    </v-expand-transition>
                                </div>

                                <transition name="fade" mode="out-in">
                                    <div v-if="isPersonalTasksLoading" key="loading-personal" class="text-center pa-8">
                                        <v-progress-circular indeterminate color="primary"
                                            size="48"></v-progress-circular>
                                        <div class="mt-4 text-subtitle-1 grey--text">Loading your tasks...</div>
                                    </div>
                                    <div v-else-if="filteredPersonalTasksForDisplay.length === 0" key="empty-personal"
                                        class="text-center pa-8">
                                        <v-icon size="64" color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
                                        <div class="text-h6 mt-4 grey--text">No tasks found</div>
                                        <div class="text-caption grey--text">Create a task to get started</div>
                                    </div>
                                    <UnifiedTreeViewDashboard v-else key="personal-tasks" taskType="personal" :hide-title="true" :hide-quick-task="true"
                                        :tasks="sortedPersonalTasksForDisplay" :group-by="personalGroupBy"
                                        :users="users()"
                                        :companies="companies()" :container-height="600" @create-task="openNewTaskDialog"
                                        @getTasks="handleTaskRefresh" @updateTaskData="updateTaskData"
                                        @optimisticUpdate="handleOptimisticUpdate" />
                                </transition>

                                <!-- Completed Tasks (latest 50, loaded separately, paginated) -->
                                <v-expansion-panels flat class="completed-tasks-section mt-4">
                                    <v-expansion-panel>
                                        <v-expansion-panel-header class="pa-2">
                                            <div class="d-flex align-center">
                                                <v-icon color="success" size="18" class="mr-2">mdi-check-circle</v-icon>
                                                <span class="text-subtitle-2 font-weight-bold">Completed Tasks</span>
                                                <v-chip x-small outlined class="ml-2">
                                                    {{ completedPersonalTasks().length }}<span v-if="completedPersonalTasksTotal"> / {{ completedPersonalTasksTotal() }}</span>
                                                </v-chip>
                                            </div>
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content>
                                            <div v-if="completedPersonalTasks().length === 0" class="text-caption grey--text pa-2">
                                                <span v-if="isLoadingCompletedPersonalTasks()">Loading completed tasks...</span>
                                                <span v-else>No completed tasks yet</span>
                                            </div>
                                            <template v-else>
                                                <UnifiedTreeViewDashboard taskType="personal" :tasks="completedPersonalTasks()" :hide-title="true"
                                                    :users="users()" :companies="companies()" :container-height="280"
                                                    :hide-quick-task="true"
                                                    @create-task="openNewTaskDialog"
                                                    @getTasks="handleTaskRefresh" @updateTaskData="updateTaskData"
                                                    @optimisticUpdate="handleOptimisticUpdate" />
                                                <div class="text-center mt-2" v-if="hasMoreCompletedPersonalTasks()">
                                                    <v-btn text small color="primary" :loading="isLoadingCompletedPersonalTasks()"
                                                        @click="loadMorePersonalCompletedTasks">
                                                        Load more
                                                    </v-btn>
                                                </div>
                                            </template>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-tab-item>

                            <!-- Tasks Assigned To Others Tab -->
                            <v-tab-item>
                                <div class="quick-task-bar mb-3">
                                    <v-btn-toggle v-model="headerQuickTaskMode" mandatory dense class="quick-task-toggle">
                                        <v-btn value="single" x-small>
                                            <v-icon small>mdi-file-plus</v-icon>
                                        </v-btn>
                                        <v-btn value="multiple" x-small>
                                            <v-icon small>mdi-file-multiple</v-icon>
                                        </v-btn>
                                    </v-btn-toggle>
                                    <v-text-field v-if="headerQuickTaskMode === 'single'" v-model="headerQuickTaskName"
                                        placeholder="Type a task name and press Enter..."
                                        dense solo flat hide-details class="filter-search-style quick-task-bar-input"
                                        data-tour="quick-task-input"
                                        :loading="headerQuickTaskCreating" :disabled="headerQuickTaskCreating"
                                        @keydown.enter="submitHeaderQuickTask">
                                        <template v-slot:prepend-inner>
                                            <v-icon small color="grey">mdi-plus</v-icon>
                                        </template>
                                    </v-text-field>
                                    <v-textarea v-else v-model="headerQuickTaskNamesText"
                                        placeholder="Enter task names, one per line..."
                                        dense solo flat rows="3" hide-details class="filter-search-style quick-task-bar-input"
                                        data-tour="quick-task-input" :disabled="headerQuickTaskCreating">
                                        <template v-slot:prepend-inner>
                                            <v-icon small color="grey">mdi-plus</v-icon>
                                        </template>
                                    </v-textarea>
                                    <v-autocomplete v-model="headerQuickTaskCompanyId" :items="[{ name: 'No Company', id: null }, ...companies()]"
                                        item-text="name" item-value="id" dense solo flat hide-details clearable
                                        placeholder="Select company (optional)" class="filter-search-style quick-task-bar-company"
                                        data-tour="quick-task-company">
                                        <template v-slot:prepend-inner>
                                            <v-icon small color="grey">mdi-domain</v-icon>
                                        </template>
                                    </v-autocomplete>

                                    <v-expand-transition>
                                        <div v-if="headerQuickTaskMode === 'multiple' && headerQuickTaskNamesList.length > 0" class="quick-task-queue">
                                            <v-chip-group column>
                                                <v-chip v-for="(name, index) in headerQuickTaskNamesList" :key="index" x-small outlined close
                                                    @click:close="removeHeaderQuickTaskName(index)">
                                                    {{ name }}
                                                </v-chip>
                                            </v-chip-group>
                                            <v-btn small color="primary" :loading="headerQuickTaskCreating" @click="createHeaderQuickTasks">
                                                <v-icon small left>mdi-plus</v-icon>
                                                Create {{ headerQuickTaskNamesList.length }} Tasks
                                            </v-btn>
                                        </div>
                                    </v-expand-transition>
                                </div>

                                <transition name="fade" mode="out-in">
                                    <div v-if="isAssignedTasksLoading" key="loading-assigned" class="text-center pa-8">
                                        <v-progress-circular indeterminate color="primary"
                                            size="48"></v-progress-circular>
                                        <div class="mt-4 text-subtitle-1 grey--text">Loading assigned tasks...</div>
                                    </div>
                                    <div v-else-if="filteredAssignedTasksForDisplay.length === 0" key="empty-assigned"
                                        class="text-center pa-8">
                                        <v-icon size="64" color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
                                        <div class="text-h6 mt-4 grey--text">No assigned tasks found</div>
                                        <div class="text-caption grey--text">Assign tasks to others to see them here
                                        </div>
                                    </div>
                                    <UnifiedTreeViewDashboard v-else key="assigned-tasks" taskType="assigned" :hide-title="true" :hide-quick-task="true"
                                        :tasks="sortedAssignedTasksForDisplay" :group-by="assignedGroupBy"
                                        :users="users()"
                                        :companies="companies()" :container-height="600" @create-task="openNewTaskDialog"
                                        @getTasks="handleTaskRefresh" @updateTaskData="updateTaskData"
                                        @optimisticUpdate="handleOptimisticUpdate" />
                                </transition>

                                <!-- Completed Tasks (latest 50, loaded separately, paginated) -->
                                <v-expansion-panels flat class="completed-tasks-section mt-4">
                                    <v-expansion-panel>
                                        <v-expansion-panel-header class="pa-2">
                                            <div class="d-flex align-center">
                                                <v-icon color="success" size="18" class="mr-2">mdi-check-circle</v-icon>
                                                <span class="text-subtitle-2 font-weight-bold">Completed Tasks</span>
                                                <v-chip x-small outlined class="ml-2">
                                                    {{ completedAssignedTasks().length }}<span v-if="completedAssignedTasksTotal"> / {{ completedAssignedTasksTotal() }}</span>
                                                </v-chip>
                                            </div>
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content>
                                            <div v-if="completedAssignedTasks().length === 0" class="text-caption grey--text pa-2">
                                                <span v-if="isLoadingCompletedAssignedTasks()">Loading completed tasks...</span>
                                                <span v-else>No completed tasks yet</span>
                                            </div>
                                            <template v-else>
                                                <UnifiedTreeViewDashboard taskType="assigned" :tasks="completedAssignedTasks()" :hide-title="true"
                                                    :users="users()" :companies="companies()" :container-height="280"
                                                    :hide-quick-task="true"
                                                    @create-task="openNewTaskDialog"
                                                    @getTasks="handleTaskRefresh" @updateTaskData="updateTaskData"
                                                    @optimisticUpdate="handleOptimisticUpdate" />
                                                <div class="text-center mt-2" v-if="hasMoreCompletedAssignedTasks()">
                                                    <v-btn text small color="primary" :loading="isLoadingCompletedAssignedTasks()"
                                                        @click="loadMoreAssignedCompletedTasks">
                                                        Load more
                                                    </v-btn>
                                                </div>
                                            </template>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-tab-item>

                            <!-- Tasks I Assigned To Others Tab -->
                            <v-tab-item>
                                <transition name="fade" mode="out-in">
                                    <div v-if="isPersonalTasksLoading" key="loading-created-for-others" class="text-center pa-8">
                                        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
                                        <div class="mt-4 text-subtitle-1 grey--text">Loading tasks...</div>
                                    </div>
                                    <div v-else-if="filteredCreatedForOthersTasksForDisplay.length === 0" key="empty-created-for-others"
                                        class="text-center pa-8">
                                        <v-icon size="64" color="grey lighten-1">mdi-account-arrow-right-outline</v-icon>
                                        <div class="text-h6 mt-4 grey--text">No pending approval tasks</div>
                                        <div class="text-caption grey--text">Tasks you created that are awaiting approval will appear here</div>
                                    </div>
                                    <UnifiedTreeViewDashboard v-else key="created-for-others-tasks" taskType="assigned" :hide-quick-task="true" :hide-status="true" :hide-title="true"
                                        :tasks="sortedCreatedForOthersTasksForDisplay" :group-by="createdForOthersGroupBy"
                                        :users="users()"
                                        :companies="companies()" @create-task="openNewTaskDialog"
                                        @getTasks="handleTaskRefresh" @updateTaskData="updateTaskData"
                                        @optimisticUpdate="handleOptimisticUpdate" />
                                </transition>
                            </v-tab-item>
                        </v-tabs-items>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-dialog v-model="delete_task_dialog" persistent max-width="500">
            <v-card>
                <v-card-title>Are you sure you want to delete this task?</v-card-title>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="closeDeleteTaskDialog()" text>Cancel</v-btn>
                    <v-btn @click="deleteTask()" color="error">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- New Enhanced Task Creation Dialog -->
        <TaskCreationDialog
            v-model="new_task_dialog"
            :users="users()"
            :companies="companies()"
            :task-type="new_task_type"
            :default-name="voiceTask.name"
            :default-description="voiceTask.description"
            :default-assignees="voiceTask.assigneeIds"
            :default-company-id="voiceTask.companyId"
            :default-priority-id="voiceTask.priorityId"
            :default-due-date="voiceTask.dueDate"
            :default-role-id="voiceTask.roleId"
            :default-mode="voiceTask.mode"
            :default-task-names="voiceTask.taskNames"
            @tasks-created="onTasksCreated"
            @close="closeNewTaskDialog"
        />
        <v-dialog v-model="delete_bulk_tasks_dialog" persistent max-width="500">
            <v-card v-if="selected_tasks">
                <v-card-title>Are you sure you want to delete these tasks?</v-card-title>
                <v-card-text>
                    <div v-for="(task, index) in selected_tasks" :key="index">
                        {{ task.name }}
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="closeDeleteBulkTasksDialog()" text>Cancel</v-btn>
                    <v-btn @click="deleteBulkTasks()" color="error">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="show_filters_dialog" persistent max-width="500">
            <v-card>
                <v-card-title></v-card-title>
                <v-card-text>
                    <v-select v-model="selected_priorities" :items="[{ name: 'None', id: null }, ...get_priorities]"
                        multiple item-text="name" item-value="id" label="Filter by priority"
                        @change="handleTaskRefresh"></v-select>
                    <v-select v-model="selected_statuses" :items="[{ name: 'None', id: null }, ...get_statuses]"
                        multiple item-text="name" item-value="id" label="Filter by status"
                        @change="handleTaskRefresh"></v-select>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="closeShowFiltersDialog()" text>Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import UnifiedTreeViewDashboard from '../modules/UnifiedTreeViewDashboard.vue'
import TaskCreationDialog from '../modules/TaskCreationDialog.vue'
import ScheduleView from './ScheduleView.vue'
import TaskFilterToolbar from './components/TaskFilterToolbar.vue'
import tourService from '@/services/tourService'
import { transcribeAudio, analyzeTaskIntent } from '@/services/voiceTaskService'

export default {
    name: 'MyWorkComponent',
    inject: [
        'users', 'updateUser', 'addUser', 'removeUser', 'refreshUsers',
        'personalTasks', 'assignedTasks', 'refreshTasks', 'refreshTasksForUser',
        'addPersonalTask', 'addAssignedTask', 'updateTaskInArrays',
        'optimisticUpdateTask', 'optimisticDeleteTask', 'optimisticCreateTask',
        'priorities', 'statuses', 'tags', 'meetingStatuses',
        'refreshPriorities', 'refreshStatuses', 'refreshTags', 'refreshMeetingStatuses',
        'companies', 'refreshCompanies',
        'roles',
        // Loading state providers
        'isLoadingUsers', 'isLoadingTasks', 'tasksLoaded',
        // Unapproved tasks (status_id = 8)
        'unapprovedTasks', 'refreshUnapprovedTasks',
        // Completed tasks (latest 50, paginated separately from active tasks)
        'completedPersonalTasks', 'completedAssignedTasks',
        'loadMoreCompletedPersonalTasks', 'loadMoreCompletedAssignedTasks',
        'isLoadingCompletedPersonalTasks', 'isLoadingCompletedAssignedTasks',
        'hasMoreCompletedPersonalTasks', 'hasMoreCompletedAssignedTasks',
        'completedPersonalTasksTotal', 'completedAssignedTasksTotal',
        // Server-computed "My Statistics" counts (single aggregate query)
        'myTaskStats', 'refreshMyTaskStats', 'isLoadingMyTaskStats',
    ],
    components: {
        UnifiedTreeViewDashboard,
        TaskCreationDialog,
        ScheduleView,
        TaskFilterToolbar,
    },
    data: () => ({
        loading: false,
        refreshing: false,
        table_loading_personal: false,
        table_loading_assigned: false,
        assignedTabLoading: false, // Add loading state for assigned tab
        isLoadingAssignedTasks: false, // New loading state for initial assigned tasks load
        api_url: null,
        is_company_selected: false,
        activeTab: 0,
        viewMode: 'tasks', // 'tasks' or 'schedule'
        selectedViewingUser: null, // For admin to view other users' tasks
        personalFilters: {
            search: '',
            company: null,
            priority: null,
            assignee: null,
            dueDate: null
        },
        assignedFilters: {
            search: '',
            company: null,
            priority: null,
            assignee: null,
            dueDate: null
        },
        createdForOthersFilters: {
            search: '',
            company: null,
            priority: null,
            dueDate: null
        },
        personalGroupBy: 'status',
        assignedGroupBy: 'status',
        createdForOthersGroupBy: 'company',
        personalSortBy: 'none',
        assignedSortBy: 'none',
        createdForOthersSortBy: 'none',
        groupByOptions: [
            { name: 'Status', value: 'status' },
            { name: 'Company', value: 'company' },
            { name: 'Assignee', value: 'assignee' },
            { name: 'Priority', value: 'priority' },
            { name: 'Due Date', value: 'due_date_bucket' },
            { name: 'Recurring', value: 'recurring' },
        ],
        groupByOptionsNoStatus: [
            { name: 'Company', value: 'company' },
            { name: 'Assignee', value: 'assignee' },
            { name: 'Priority', value: 'priority' },
            { name: 'Due Date', value: 'due_date_bucket' },
            { name: 'Recurring', value: 'recurring' },
        ],
        sortByOptions: [
            { name: 'None', value: 'none' },
            { name: 'Due Date (soonest)', value: 'due_date_asc' },
            { name: 'Priority (highest)', value: 'priority_desc' },
            { name: 'Name (A-Z)', value: 'name_asc' },
            { name: 'Created (newest)', value: 'created_desc' },
        ],
        dueDateFilters: [
            { name: 'All', value: null },
            { name: 'Overdue', value: 'overdue' },
            { name: 'Due Today', value: 'today' },
            { name: 'Due This Week', value: 'week' },
            { name: 'Due This Month', value: 'month' },
        ],
        selected_tasks: [],
        selected_assignee: {
            id: null,
            name: null,
            access: [],
        },
        due_date_menu: [],
        due_date_menu_assigned: [],
        new_task_dialog: false,
        new_task_type: 'assign',
        task_headers: [
            { text: "", width: "1%", sortable: false },
            { text: "Task Name", value: "name", width: "41%", sortable: false },
            { text: "Due Date", value: "due_date", width: "15%", sortable: false },
            {
                text: "Allocated Time",
                value: "allocated_time",
                width: "12%",
                sortable: false,
            },
            { text: "Priority", value: "priority_id", width: "14%", sortable: false },
            { text: "Status", value: "status_id", width: "17%", sortable: false },
            { text: "", width: "1%", sortable: false },
        ],
        header_personal: [
            { text: "Task", value: "name", width: "10%" },
            { text: "Due Date", value: "due_date", width: "5%" },
            { text: "Date created", value: "created", width: "5%" },
            { text: "Company", value: "company", width: "5%" },
            { text: "Assignees", value: "assignee", width: "5%" },
            { text: "Status", value: "status", width: "5%" },
            { text: "Priority", value: "priority", width: "5%", sortable: true },
            { text: "", value: "view", width: "5%" },
        ],
        header_assigned: [
            { text: "Task", value: "name", width: "10%" },
            { text: "Due Date", value: "due_date", width: "5%" },
            { text: "Date created", value: "created", width: "5%" },
            { text: "Company", value: "company", width: "5%" },
            { text: "Assignees", value: "assignee", width: "5%" },
            { text: "Status", value: "status", width: "5%" },
            { text: "Tag", value: "tags", width: "5%" },
            { text: "Priority", value: "priority", width: "5%", sortable: true },
            { text: "", value: "view", width: "5%" },
        ],
        delete_task_dialog: false,
        task_to_delete: null,
        task_details_dialog: false,
        original_selected_task: null,
        edit_bulk_tasks_dialog: false,
        delete_bulk_tasks_dialog: false,
        can_close_task_details_dialog: false,
        navigation: {
            width: 600,
            borderSize: 5,
        },
        show_filters_dialog: false,
        create_quick_task: false,
        selected_assign_filter: "",
        chooseUserModel: false,
        searchUser: null,
        // Header quick-task creation (replaces UnifiedTreeViewDashboard's own
        // quick-task row for the two active list/calendar tabs it applies to —
        // see :hide-quick-task on those UnifiedTreeViewDashboard usages).
        headerQuickTaskMode: 'single',
        headerQuickTaskName: '',
        headerQuickTaskNamesText: '',
        headerQuickTaskCompanyId: null,
        headerQuickTaskCreating: false,
        // Voice-to-task state
        isRecording: false,
        isProcessingVoice: false,
        voiceStatus: '',
        mediaRecorder: null,
        audioChunks: [],
        voiceTask: {
            name: '',
            description: '',
            type: 'assign',
            assigneeIds: [],
            companyId: null,
            priorityId: null,
            dueDate: null,
            roleId: null,
            mode: 'single',
            taskNames: [],
        },
    }),
    watch: {
        // Watch for tasks to be loaded and then start tour if needed
        tasksLoaded: {
            handler(newValue) {
                if (newValue) {
                    this.checkAndStartTour()
                }
            },
            immediate: true
        },
        // Re-initialize default status filters when statuses are loaded
        statusesData: {
            handler(newStatuses) {
                if (newStatuses && newStatuses.length > 0) {
                    this.initializeDefaultStatusFilters();
                }
            },
            immediate: true
        },
        // Watch for tab changes to show loader for assigned tasks
        activeTab(newTab) {
            if (newTab === 1) {
                const hasAssignedTasks = (this.assignedTasks() || []).length > 0

                if (!hasAssignedTasks && !this.isLoadingAssignedTasks) {
                    // Show loader and refresh assigned tasks if they haven't been loaded yet
                    this.isLoadingAssignedTasks = true;
                    this.loadAssignedTasksInBackground();
                }
            }
        },
    },
    async created() {
        this.api_url = process.env.VUE_APP_API_URL
    },
    async mounted() {
        document.title = "DG Tasks - My Work"

        // Preselect a tab/view when arriving via a Home quick-link
        // (?tab=0|1|2&view=tasks|schedule); defaults to today's 0/'tasks' when absent.
        const queryTab = parseInt(this.$route.query.tab, 10);
        if (!Number.isNaN(queryTab) && queryTab >= 0 && queryTab <= 2) {
            this.activeTab = queryTab;
        }
        if (this.$route.query.view === 'schedule' || this.$route.query.view === 'tasks') {
            this.viewMode = this.$route.query.view;
        }

        // Register component with tour service for interactive tours
        tourService.setContext(this.$router, this)

        // Initialize default status filters (all except Completed)
        this.initializeDefaultStatusFilters()

        // Re-pull "My Statistics" every time the Dashboard is (re)entered, so
        // switching tabs and coming back always shows current server counts.
        if (typeof this.refreshMyTaskStats === 'function') {
            this.refreshMyTaskStats();
        }

        // Auto-start essentials tour for new users - wait for tasks to be loaded
        this.checkAndStartTour()
    },
    computed: {
        // v-tabs binds by index; viewMode itself stays a plain 'tasks'/'schedule'
        // string data property (tourService.js sets it directly by that name).
        // Single-row work tab (My Task List / My Calendar View / Assigned Task
        // List / Assigned Calendar View / Pending Requests), derived from —
        // and writing back into — the real underlying `activeTab`/`viewMode`
        // data properties. Those two stay the actual source of truth (not
        // replaced) because tourService.js reads/writes them directly by
        // name (`componentInstance.activeTab = ...`, `.viewMode = ...`);
        // keeping them real means the tour keeps working even though the UI
        // no longer has two separate tab rows for them.
        activeWorkTab: {
            get() {
                if (this.activeTab === 2) return 4; // Pending Requests (list only)
                if (this.activeTab === 1) return this.viewMode === 'schedule' ? 3 : 2; // Assigned
                return this.viewMode === 'schedule' ? 1 : 0; // My Tasks
            },
            set(index) {
                const map = [
                    { activeTab: 0, viewMode: 'tasks' },    // My Task List
                    { activeTab: 0, viewMode: 'schedule' }, // My Calendar View
                    { activeTab: 1, viewMode: 'tasks' },    // Assigned Task List
                    { activeTab: 1, viewMode: 'schedule' }, // Assigned Calendar View
                    { activeTab: 2, viewMode: 'tasks' },    // Pending Requests
                ];
                const target = map[index];
                if (!target) return;
                this.activeTab = target.activeTab;
                this.viewMode = target.viewMode;
            },
        },
        ...mapGetters({
            get_user: "auth/get_user",
            get_selected_company: "companies/get_selected_company",
            get_priorities: "tasks/get_priorities",
            get_statuses: "tasks/get_statuses",
            get_selected_priorities: "tasks/get_selected_priorities",
            get_selected_statuses: "tasks/get_selected_statuses",
            get_personal_status_filters: "tasks/get_personal_status_filters",
            get_assigned_status_filters: "tasks/get_assigned_status_filters",
            get_created_for_others_status_filters: "tasks/get_created_for_others_status_filters",
            selectedUserGetters: "tasks/getUserId",
            getterTag: "tasks/getTags",
            userStats: "auth/get_user_stats",
            get_meeting_statuses: "meetings/get_meeting_statuses",
            get_preferences: "auth/get_preferences",
        }),
        // Loading state computed properties that check if data is actually available
        isPersonalTasksLoading() {
            // Consider loading if:
            // 1. DefaultLayout is still loading tasks from API, OR
            // 2. Tasks haven't been loaded at least once yet, OR
            // 3. Manual loading flag is set (for user switching), OR
            // 4. personalTasks function is not available yet
            if (this.isLoadingTasks && this.isLoadingTasks()) return true
            if (this.tasksLoaded && !this.tasksLoaded()) return true
            if (this.table_loading_personal) return true
            if (!this.personalTasks) return true

            const tasks = this.personalTasks()
            const isLoading = tasks === undefined || tasks === null
            return isLoading
        },
        isAssignedTasksLoading() {
            // Consider loading if:
            // 1. DefaultLayout is still loading tasks from API, OR
            // 2. Tasks haven't been loaded at least once yet, OR
            // 3. Manual loading flags are set (for user switching or manual loading), OR
            // 4. assignedTasks function is not available yet
            if (this.isLoadingTasks && this.isLoadingTasks()) return true
            if (this.tasksLoaded && !this.tasksLoaded()) return true
            if (this.table_loading_assigned || this.isLoadingAssignedTasks) return true
            if (!this.assignedTasks) return true

            const tasks = this.assignedTasks()
            const isLoading = tasks === undefined || tasks === null
            return isLoading
        },
        // Computed wrappers for injected reference data
        prioritiesData() {
            return this.priorities ? this.priorities() : [];
        },
        statusesData() {
            return this.statuses ? this.statuses() : [];
        },
        tagsData() {
            return this.tags ? this.tags() : [];
        },
        selected_priorities: {
            get() {
                return this.get_selected_priorities;
            },
            set(new_priorities) {
                this.$store.dispatch("tasks/set_selected_priorities", new_priorities);
            },
        },
        selected_statuses: {
            get() {
                return this.get_selected_statuses;
            },
            set(new_statuses) {
                this.$store.dispatch("tasks/set_selected_statuses", new_statuses);
            },
        },
        // Computed properties for dashboard status filters
        personalStatusFilters: {
            get() {
                return this.get_personal_status_filters;
            },
            set(filters) {
                // Ensure at least one status is always selected
                if (!filters || filters.length === 0) {
                    // If trying to clear all, keep the current selection
                    return;
                }

                this.$store.dispatch("tasks/set_personal_status_filters", filters);
            },
        },
        assignedStatusFilters: {
            get() {
                return this.get_assigned_status_filters;
            },
            set(filters) {
                // Ensure at least one status is always selected
                if (!filters || filters.length === 0) {
                    // If trying to clear all, keep the current selection
                    return;
                }

                this.$store.dispatch("tasks/set_assigned_status_filters", filters);
            },
        },
        createdForOthersStatusFilters: {
            get() {
                return this.get_created_for_others_status_filters;
            },
            set(filters) {
                if (!filters || filters.length === 0) {
                    return;
                }
                this.$store.dispatch("tasks/set_created_for_others_status_filters", filters);
            },
        },
        prioritiesSelect() {
            return [{ name: "None", id: null }, ...(this.prioritiesData || [])];
        },
        statusesSelect() {
            return [{ name: "None", id: null }, ...(this.statusesData || [])];
        },
        tagsSelect() {
            return [{ name: "None", id: null }, ...(this.tagsData || [])];
        },
        filteredUsers() {
            if (this.searchUser !== null) {
                return this.users().filter((q) =>
                    q.name.toLowerCase().includes(this.searchUser.toLowerCase())
                );
            }
            return this.users();
        },
        filteredPersonalTasks() {
            return this.personalTasks() || [];
        },
        filteredAssignedTasks() {
            return this.assignedTasks() || [];
        },
        // "My Statistics" card — a straight read of the server-computed counts
        // (single aggregate query, scoped to "my" tasks the same way My Task List
        // is: assignee, excluding pending-approval). No client-side counting or
        // optimistic math; DefaultLayout re-fetches this on mount and after any
        // action that changes a task's status, so it stays accurate without it.
        liveStats() {
            const stats = this.myTaskStats ? this.myTaskStats() : {};
            return {
                todo: stats.todo_tasks || 0,
                inProgress: stats.in_progress_tasks || 0,
                completed: stats.completed_tasks || 0,
                overdue: stats.overdue_tasks || 0,
            };
        },
        // Filter options — exclude 'For Approval' (status_id=8) and 'Completed' (status_id=4)
        // from My Task List & Tasks Assigned To Others; completed tasks get their own table.
        statusFilterOptions() {
            return (this.statusesData || []).filter(s => s.id !== 8 && s.id !== 4);
        },
        companyFilterOptions() {
            const companies = this.companies() || [];
            return [{ name: 'All Companies', id: null }, ...companies];
        },
        priorityFilterOptions() {
            return [{ name: 'All Priorities', id: null }, ...(this.prioritiesData || [])];
        },
        assigneeFilterOptions() {
            return this.users() || [];
        },
        // Search now lives in the header (next to "View tasks for user")
        // rather than in each tab's TaskFilterToolbar; this routes it into
        // whichever of the three per-tab filter objects is currently active.
        activeSearchQuery: {
            get() {
                if (this.activeTab === 1) return this.assignedFilters.search;
                if (this.activeTab === 2) return this.createdForOthersFilters.search;
                return this.personalFilters.search;
            },
            set(value) {
                if (this.activeTab === 1) this.assignedFilters.search = value;
                else if (this.activeTab === 2) this.createdForOthersFilters.search = value;
                else this.personalFilters.search = value;
            },
        },
        headerQuickTaskNamesList() {
            return this.headerQuickTaskNamesText
                .split('\n')
                .map(name => name.trim())
                .filter(name => name.length > 0);
        },
        // Filtered tasks for display
        filteredPersonalTasksForDisplay() {
            // Exclude pending-approval tasks (status_id=8) — those belong in the third tab
            let baseTasks = (this.personalTasks() || []).filter(t => t.status_id !== 8);

            const filtered = this.applyPersonalFilters(baseTasks);
            return filtered;
        },
        sortedPersonalTasksForDisplay() {
            return this.sortTasks(this.filteredPersonalTasksForDisplay, this.personalSortBy);
        },
        filteredAssignedTasksForDisplay() {
            // Exclude pending-approval tasks (status_id=8) — those belong in the third tab
            let baseTasks = (this.assignedTasks() || []).filter(t => t.status_id !== 8);

            const filtered = this.applyAssignedFilters(baseTasks);
            return filtered;
        },
        sortedAssignedTasksForDisplay() {
            return this.sortTasks(this.filteredAssignedTasksForDisplay, this.assignedSortBy);
        },
        filteredCreatedForOthersTasksForDisplay() {
            // Tasks with status_id = 8 (Pending Approval) that I created
            // These live in unapprovedTasks(), not personalTasks()
            const myId = this.get_user ? this.get_user.id : null;
            const baseTasks = (this.unapprovedTasks ? this.unapprovedTasks() || [] : []).filter(task => {
                if (!myId) return false;
                return task.user_id === myId;
            });
            return this.applyCreatedForOthersFilters(baseTasks);
        },
        sortedCreatedForOthersTasksForDisplay() {
            return this.sortTasks(this.filteredCreatedForOthersTasksForDisplay, this.createdForOthersSortBy);
        },
    },
    methods: {
        canViewUserTasks(userId) {
            const currentUser = this.get_user;
            if (!currentUser) return false;

            const currentUserId = currentUser.id;
            const restrictedUserIds = [2, 4];

            // If the target user is not in the restricted list, everyone can see their tasks
            if (!restrictedUserIds.includes(userId)) {
                return true;
            }

            // If viewing user 2's tasks
            if (userId === 2) {
                // Only user 2 can see their own tasks
                return currentUserId === 2;
            }

            // If viewing user 4's tasks  
            if (userId === 4) {
                // User 2 can see user 4's tasks, and user 4 can see their own tasks
                return currentUserId === 2 || currentUserId === 4;
            }

            return false;
        },
        async onUserSelectionChange(userId) {
            // When admin selects a user to view their tasks
            this.table_loading_personal = true;
            this.table_loading_assigned = true;

            try {
                if (userId) {
                    // Check permissions before loading tasks
                    if (!this.canViewUserTasks(userId)) {
                        this.$toast.error('You do not have permission to view this user\'s tasks');
                        this.selectedViewingUser = null;
                        this.table_loading_personal = false;
                        this.table_loading_assigned = false;
                        return;
                    }

                    // Pass is_viewing=true to indicate we're viewing another user's tasks
                    await this.refreshTasksForUser(userId, false, true);
                } else {
                    // Cleared selection, go back to logged-in user's tasks
                    await this.refreshTasks();
                }
            } catch (error) {
                console.error('Error switching user view:', error);
                this.$toast.error('Failed to load tasks for selected user');
            } finally {
                this.table_loading_personal = false;
                this.table_loading_assigned = false;
            }
        },
        async setUser(user) {
            this.$store.dispatch("tasks/set_user_id", user);
            // Use injected refresh method instead of local getTasks
            if (typeof this.refreshTasks === 'function') {
                await this.refreshTasks();
            }
        },
        checkSelectedCompany() {
            // Set filter based on user role, regardless of company selection
            if (this.get_user.role_id < 3) {
                this.selected_assign_filter = "All";
            } else {
                this.selected_assign_filter = "My";
            }
        },
        async refreshData() {
            this.refreshing = true;
            try {
                await Promise.all([
                    this.refreshTasks(),
                    this.refreshUsers(),
                    this.refreshStatuses(),
                    this.refreshPriorities(),
                    // this.refreshTags(),
                    this.refreshCompanies(),
                ]);
                this.$toast.success('Data refreshed successfully');
            } catch (error) {
                console.error('Error refreshing data:', error);
                this.$toast.error('Failed to refresh data');
            } finally {
                this.refreshing = false;
            }
        },
        // Default status IDs for tabs 1/2 (personal/assigned): all statuses
        // except "Completed" and "For Approval" (status_id=8).
        getDefaultActiveStatusIds() {
            const statuses = this.statusesData || [];
            const completedStatus = statuses.find(s => s.name && s.name.toString().toLowerCase().trim() === 'completed');
            return statuses
                .filter(status => {
                    if (status.id === 8) return false;
                    if (completedStatus) return status.id !== completedStatus.id;
                    return true;
                })
                .map(status => status.id);
        },
        initializeDefaultStatusFilters() {
            // Set default status filters to all statuses except the one named "Completed" (case-insensitive)
            const statuses = this.statusesData || [];
            if (statuses && statuses.length > 0) {
                const defaultStatuses = this.getDefaultActiveStatusIds();

                // Set for personal and assigned filters only if they are currently empty
                if (!this.personalStatusFilters || this.personalStatusFilters.length === 0) {
                    this.personalStatusFilters = [...defaultStatuses];
                }
                if (!this.assignedStatusFilters || this.assignedStatusFilters.length === 0) {
                    this.assignedStatusFilters = [...defaultStatuses];
                }
                // For "Tasks I Assigned To Others", show ALL statuses by default so the user can
                // see the full lifecycle: Pending Approval → Accepted (active) → Completed / Rejected
                if (!this.createdForOthersStatusFilters || this.createdForOthersStatusFilters.length === 0) {
                    this.createdForOthersStatusFilters = statuses.map(s => s.id);
                }
            }
        },

        // Central method to handle task refresh requests from child components
        handleTaskRefresh() {
            if (typeof this.refreshTasks === 'function') {
                this.refreshTasks();
            } else {
                console.warn('[Dashboard] refreshTasks method not available from injection');
            }
        },



        async loadAssignedTasksInBackground() {
            // Check if assigned tasks are already loaded
            const hasAssignedTasks = (this.assignedTasks() || []).length > 0

            if (hasAssignedTasks) {
                this.isLoadingAssignedTasks = false
                return
            }

            this.isLoadingAssignedTasks = true
            try {
                // Use DefaultLayout's refresh method instead
                if (typeof this.refreshTasks === 'function') {
                    await this.refreshTasks()
                }
            } catch (error) {
                console.error('Error loading assigned tasks in background:', error)
            } finally {
                this.isLoadingAssignedTasks = false
            }
        },

        async loadMorePersonalCompletedTasks() {
            if (typeof this.loadMoreCompletedPersonalTasks === 'function') {
                try {
                    await this.loadMoreCompletedPersonalTasks();
                } catch (error) {
                    console.error('Error loading more completed personal tasks:', error);
                    this.$toast.error('Failed to load more completed tasks');
                }
            }
        },
        async loadMoreAssignedCompletedTasks() {
            if (typeof this.loadMoreCompletedAssignedTasks === 'function') {
                try {
                    await this.loadMoreCompletedAssignedTasks();
                } catch (error) {
                    console.error('Error loading more completed assigned tasks:', error);
                    this.$toast.error('Failed to load more completed tasks');
                }
            }
        },
        // Header quick-task creation — mirrors UnifiedTreeViewDashboard's own
        // createQuickTask, scoped down to what My Work actually needs (no
        // milestone/goal/meeting context) and routed through the shared
        // optimisticCreateTask provider.
        submitHeaderQuickTask() {
            if (!this.headerQuickTaskName || !this.headerQuickTaskName.trim()) return;
            const name = this.headerQuickTaskName.trim();
            this.headerQuickTaskName = '';
            this.createSingleQuickTask(name);
        },
        removeHeaderQuickTaskName(index) {
            const lines = this.headerQuickTaskNamesText.split('\n');
            // headerQuickTaskNamesList is the trimmed/filtered view of these lines;
            // map its index back to the matching non-empty line to remove.
            let seen = -1;
            const kept = lines.filter(line => {
                if (line.trim().length === 0) return true;
                seen++;
                return seen !== index;
            });
            this.headerQuickTaskNamesText = kept.join('\n');
        },
        async createHeaderQuickTasks() {
            if (this.headerQuickTaskNamesList.length === 0) return;
            const names = [...this.headerQuickTaskNamesList];
            this.headerQuickTaskNamesText = '';
            this.headerQuickTaskCreating = true;
            try {
                for (const name of names) {
                    await this.createSingleQuickTask(name);
                }
            } finally {
                this.headerQuickTaskCreating = false;
            }
        },
        async createSingleQuickTask(name) {
            // Activeworktab 0/1 = My Task List/Calendar (personal); 2/3 = Assigned (assigned).
            const isPersonal = this.activeTab === 0;
            const payload = {
                name,
                description: null,
                priority_id: null,
                status_id: 1,
                due_date: null,
                company_id: this.headerQuickTaskCompanyId,
                project_id: null,
                assignee_ids: isPersonal ? [this.get_user.id] : [],
                allocated_time: null,
            };
            this.headerQuickTaskCreating = true;
            try {
                await this.optimisticCreateTask(payload, isPersonal, async () => {
                    const response = await this.$axios.post('api/tasks/create_task_v2', { tasks: [payload] });
                    if (!response.data.response) {
                        throw new Error(response.data.message || 'Failed to create task');
                    }
                    return { data: Array.isArray(response.data.data) ? response.data.data[0] : response.data.data };
                });
            } catch (error) {
                console.error('[MyWork] quick task creation failed:', error);
                this.$toast.error(error.message || 'Failed to create task');
            } finally {
                this.headerQuickTaskCreating = false;
            }
        },
        openNewTaskDialog(type = 'assign') {
            this.new_task_type = type
            this.new_task_dialog = true
        },
        closeNewTaskDialog() {
            this.new_task_dialog = false
            // Clear voice task data so next manual open is blank
            this.voiceTask = { name: '', description: '', type: 'assign', assigneeIds: [], companyId: null, priorityId: null, dueDate: null, roleId: null, mode: 'single', taskNames: [] }
        },

        // ─── Voice-to-Task ─────────────────────────────────────────────────────────
        async toggleVoiceRecording() {
            if (this.isRecording) {
                this.stopVoiceRecording();
            } else {
                await this.startVoiceRecording();
            }
        },
        async startVoiceRecording() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                this.audioChunks = [];
                // Prefer webm for broad browser support
                const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
                this.mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
                this.mediaRecorder.ondataavailable = (e) => {
                    if (e.data && e.data.size > 0) this.audioChunks.push(e.data);
                };
                this.mediaRecorder.onstop = async () => {
                    // Stop all audio tracks to release mic
                    stream.getTracks().forEach(track => track.stop());
                    const blob = new Blob(this.audioChunks, { type: this.mediaRecorder.mimeType || 'audio/webm' });
                    await this.processVoiceInput(blob);
                };
                this.mediaRecorder.start();
                this.isRecording = true;
            } catch (err) {
                if (err.name === 'NotAllowedError') {
                    this.$toast.error('Microphone permission denied. Please allow microphone access and try again.');
                } else {
                    this.$toast.error('Could not access microphone: ' + (err.message || err));
                }
            }
        },
        stopVoiceRecording() {
            if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop();
            }
            this.isRecording = false;
        },
        async processVoiceInput(audioBlob) {
            this.isProcessingVoice = true;
            this.voiceStatus = 'Transcribing...';
            this.$toast.info('Transcribing your voice recording...', { timeout: 4000 });
            try {
                // 1. Transcribe
                const transcription = await transcribeAudio(audioBlob);
                if (!transcription) {
                    this.$toast.warning('No speech detected. Please try again.');
                    return;
                }
                this.$toast.success(`Transcribed: "${transcription}"`, { timeout: 5000 });

                // 2. Analyze intent
                this.voiceStatus = 'Analysing task intent...';
                this.$toast.info('Analysing task intent with AI...', { timeout: 5000 });
                const allUsers = this.users ? this.users() : [];
                const allCompanies = this.companies ? this.companies() : [];
                const allRoles = this.roles ? this.roles() : [];
                // Use local date (not UTC) to avoid off-by-one in Ireland (UTC+1)
                const now = new Date();
                const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                const result = await analyzeTaskIntent(transcription, allUsers, allCompanies, today, allRoles);

                // 3. Populate voiceTask
                this.voiceTask = {
                    name: result.name || '',
                    description: result.description || '',
                    type: result.task_type || 'assign',
                    assigneeIds: result.assignee_ids || [],
                    companyId: result.company_id || null,
                    priorityId: result.priority_id || null,
                    dueDate: result.due_date || null,
                    roleId: result.role_id || null,
                    mode: result.tasks && result.tasks.length > 1 ? 'multiple' : 'single',
                    taskNames: result.tasks && result.tasks.length > 1 ? result.tasks : [],
                };

                // 4. Open dialog — resetForm() will pick up voiceTask values via props
                this.openNewTaskDialog(this.voiceTask.type);
            } catch (err) {
                console.error('[VoiceTask] Error:', err);
                this.$toast.error(err.message || 'Voice processing failed. Please try again.');
            } finally {
                this.isProcessingVoice = false;
                this.voiceStatus = '';
            }
        },
        // ───────────────────────────────────────────────────────────────────────────
        
        // Tour methods
        checkAndStartTour() {
            // Only auto-start for users who haven't seen this tour before (new
            // users, or those who haven't completed/skipped it yet). Manually
            // starting a tour from the Guided Tours menu bypasses this check.
            if (this.tasksLoaded && this.tasksLoaded() && !tourService.hasSeenTour('essentials')) {
                setTimeout(() => {
                    this.startEssentialsTour()
                }, 1500) // Shorter delay since tasks are already loaded
            }
        },
        
        startEssentialsTour() {
            tourService.startTour('essentials')
        },
        
        startAdvancedTour() {
            tourService.startTour('advanced')
        },
        onTasksCreated(newTasks) {
            // Add new tasks to appropriate lists using injected methods
            if (Array.isArray(newTasks)) {
                newTasks.forEach(task => {
                    if (this.new_task_type === 'self') {
                        this.addPersonalTask(task);
                    } else {
                        this.addAssignedTask(task);
                    }
                    // Update stats optimistically
                    this.optimisticStatsForCreate(task);
                });
            }

            // Optionally refresh from server in background
            setTimeout(() => {
                // Use injected refresh method instead of local getTasks
                if (typeof this.refreshTasks === 'function') {
                    this.refreshTasks();
                }
            }, 1000);
        },
        updateTaskData({ taskId, updatedData }) {
            // Use injected method to update task in provider arrays
            this.updateTaskInArrays(taskId, updatedData);
        },
        async updateTask(item) {
            if (this.optimisticUpdateTask) {
                // Use optimistic update pattern from DefaultLayout
                const taskUpdates = {
                    h_id: item.h_id,
                    company_id: item.company_id,
                    assignee_id: item.assignee_id,
                    project_id: item.project_id,
                    name: item.name,
                    status_id: item.status_id,
                    description: item.description,
                    priority_id: item.priority_id,
                    due_date: item.due_date,
                    allocated_time: item.allocated_time,
                    tags: item.tags,
                };

                try {
                    await this.optimisticUpdateTask(
                        item.id,
                        taskUpdates,
                        async () => {
                            const response = await this.$axios.post(`api/tasks/update_task/${item.id}`, taskUpdates);
                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }
                            return response.data;
                        }
                    );
                } catch (error) {
                    this.$toast.error(error.message || 'Failed to update task');
                }
            } else {
                // Fallback to original implementation
                let payload = {
                    h_id: item.h_id,
                    company_id: item.company_id,
                    assignee_id: item.assignee_id,
                    project_id: item.project_id,
                    name: item.name,
                    status_id: item.status_id,
                    description: item.description,
                    priority_id: item.priority_id,
                    due_date: item.due_date,
                    allocated_time: item.allocated_time,
                    tags: item.tags,
                }
                await this.$axios.post(`api/tasks/update_task/${item.id}`, payload)
                    .then(({ data }) => {
                        if (data.response) {
                            this.$toast.success(data.message)
                            // Task already updated in local arrays, no need to refresh
                        } else {
                            this.$toast.error(data.message)
                        }
                    });
            }
        },
        selectTask(item) {
            let index = this.selected_tasks.findIndex((x) => x.id == item.id)
            if (index === -1) {
                this.selected_tasks.push(item)
            } else {
                this.selected_tasks.splice(index, 1)
            }
        },
        openEditBulkTasksDialog() {
            this.edit_bulk_tasks_dialog = true
        },
        closeEditBulkTasksDialog() {
            this.edit_bulk_tasks_dialog = false
        },
        openDeleteBulkTasksDialog() {
            this.delete_bulk_tasks_dialog = true
        },
        closeDeleteBulkTasksDialog() {
            this.delete_bulk_tasks_dialog = false
        },
        async deleteBulkTasks() {
            if (!this.selected_tasks || this.selected_tasks.length === 0) {
                this.$toast.error('No tasks selected');
                return;
            }

            // Store tasks for rollback
            const tasksToDelete = [...this.selected_tasks];
            const taskIds = tasksToDelete.map(task => task.id);

            // Clear selection and close dialog immediately
            this.selected_tasks = [];
            this.closeDeleteBulkTasksDialog();

            // Optimistic delete - remove from arrays immediately if possible
            if (this.optimisticDeleteTask) {
                // Use optimistic delete for each task
                const deletePromises = taskIds.map(taskId =>
                    this.optimisticDeleteTask(taskId, async () => {
                        // This will be called for each task, but we'll do bulk delete
                        return { success: true };
                    })
                );

                try {
                    // Wait for optimistic deletes to complete
                    await Promise.all(deletePromises);

                    // Now make the actual API call
                    const response = await this.$axios.post("api/tasks/delete_tasks", tasksToDelete);

                    if (response.data.response) {
                        this.$toast.success(response.data.message);
                        // Tasks already removed optimistically, no need to refresh
                    } else {
                        throw new Error(response.data.message);
                    }
                } catch (error) {
                    // The optimistic delete methods will handle rollback
                    console.error('Error deleting tasks:', error);
                    this.$toast.error(error.message || 'Failed to delete tasks');
                }
            } else {
                // Fallback to original implementation
                await this.$axios.post("api/tasks/delete_tasks", tasksToDelete)
                    .then(({ data }) => {
                        if (data.response) {
                            this.$toast.success(data.message);
                            // Tasks already handled in local arrays
                        } else {
                            this.$toast.error(data.message);
                        }
                    });
            }
        },
        openShowFiltersDialog() {
            this.show_filters_dialog = true
        },
        closeShowFiltersDialog() {
            this.show_filters_dialog = false
        },

        // Optimistic updates for statistics
        handleOptimisticUpdate(updateData) {
            const { type, taskData, previousData, field, newValue, previousValues } = updateData;

            switch (type) {
                case 'create':
                    this.optimisticStatsForCreate(taskData);
                    break;
                case 'update':
                    this.optimisticStatsForUpdate(taskData, previousData);
                    break;
                case 'delete':
                    this.optimisticStatsForDelete(taskData);
                    break;
                case 'bulk_update':
                    this.optimisticStatsForBulkUpdate(field, newValue, previousValues);
                    break;
            }
        },
        optimisticStatsForCreate(taskData) {
            const currentStats = { ...this.userStats };
            
            // Increment appropriate status count based on task status
            if (taskData.status_id === 1) { // Todo
                currentStats.todo_tasks = (currentStats.todo_tasks || 0) + 1;
            } else if (taskData.status_id === 2) { // In Progress
                currentStats.in_progress_tasks = (currentStats.in_progress_tasks || 0) + 1;
            } else if (taskData.status_id === 3) { // In Review
                currentStats.in_review_tasks = (currentStats.in_review_tasks || 0) + 1;
            } else if (taskData.status_id === 4) { // Completed
                currentStats.completed_tasks = (currentStats.completed_tasks || 0) + 1;
            } else if (taskData.status_id === 5) { // Cancelled
                currentStats.cancelled_tasks = (currentStats.cancelled_tasks || 0) + 1;
            } else if (taskData.status_id === 6) { // Blocked
                currentStats.blocked_tasks = (currentStats.blocked_tasks || 0) + 1;
            }

            this.$store.dispatch('auth/set_user_stats', currentStats);
        },
        optimisticStatsForUpdate(newTaskData, oldTaskData) {
            const currentStats = { ...this.userStats };

            // Handle status changes
            if (oldTaskData.status_id !== newTaskData.status_id) {
                // Remove from old status
                if (oldTaskData.status_id === 1) { // Todo
                    currentStats.todo_tasks = Math.max(0, (currentStats.todo_tasks || 0) - 1);
                } else if (oldTaskData.status_id === 2) { // In Progress
                    currentStats.in_progress_tasks = Math.max(0, (currentStats.in_progress_tasks || 0) - 1);
                } else if (oldTaskData.status_id === 3) { // In Review
                    currentStats.in_review_tasks = Math.max(0, (currentStats.in_review_tasks || 0) - 1);
                } else if (oldTaskData.status_id === 4) { // Completed
                    currentStats.completed_tasks = Math.max(0, (currentStats.completed_tasks || 0) - 1);
                } else if (oldTaskData.status_id === 5) { // Cancelled
                    currentStats.cancelled_tasks = Math.max(0, (currentStats.cancelled_tasks || 0) - 1);
                } else if (oldTaskData.status_id === 6) { // Blocked
                    currentStats.blocked_tasks = Math.max(0, (currentStats.blocked_tasks || 0) - 1);
                }

                // Add to new status
                if (newTaskData.status_id === 1) { // Todo
                    currentStats.todo_tasks = (currentStats.todo_tasks || 0) + 1;
                } else if (newTaskData.status_id === 2) { // In Progress
                    currentStats.in_progress_tasks = (currentStats.in_progress_tasks || 0) + 1;
                } else if (newTaskData.status_id === 3) { // In Review
                    currentStats.in_review_tasks = (currentStats.in_review_tasks || 0) + 1;
                } else if (newTaskData.status_id === 4) { // Completed
                    currentStats.completed_tasks = (currentStats.completed_tasks || 0) + 1;
                } else if (newTaskData.status_id === 5) { // Cancelled
                    currentStats.cancelled_tasks = (currentStats.cancelled_tasks || 0) + 1;
                } else if (newTaskData.status_id === 6) { // Blocked
                    currentStats.blocked_tasks = (currentStats.blocked_tasks || 0) + 1;
                }
            }

            this.$store.dispatch('auth/set_user_stats', currentStats);
        },
        optimisticStatsForDelete(taskData) {
            const currentStats = { ...this.userStats };
            
            // Decrement appropriate status count based on deleted task status
            if (taskData.status_id === 1) { // Todo
                currentStats.todo_tasks = Math.max(0, (currentStats.todo_tasks || 0) - 1);
            } else if (taskData.status_id === 2) { // In Progress
                currentStats.in_progress_tasks = Math.max(0, (currentStats.in_progress_tasks || 0) - 1);
            } else if (taskData.status_id === 3) { // In Review
                currentStats.in_review_tasks = Math.max(0, (currentStats.in_review_tasks || 0) - 1);
            } else if (taskData.status_id === 4) { // Completed
                currentStats.completed_tasks = Math.max(0, (currentStats.completed_tasks || 0) - 1);
            } else if (taskData.status_id === 5) { // Cancelled
                currentStats.cancelled_tasks = Math.max(0, (currentStats.cancelled_tasks || 0) - 1);
            } else if (taskData.status_id === 6) { // Blocked
                currentStats.blocked_tasks = Math.max(0, (currentStats.blocked_tasks || 0) - 1);
            }

            this.$store.dispatch('auth/set_user_stats', currentStats);
        },

        optimisticStatsForBulkUpdate(field, newValue, previousValues) {
            // For bulk updates, we process each task individually
            if (field === 'status_id') {
                const currentStats = { ...this.userStats };

                // Process each task's status change
                previousValues.forEach(({ statusId: oldStatusId }) => {
                    // Remove from old status count
                    if (oldStatusId === 2) { // In Progress
                        currentStats.in_progress_tasks = Math.max(0, (currentStats.in_progress_tasks || 0) - 1);
                    } else if (oldStatusId === 4) { // Completed
                        currentStats.completed_tasks = Math.max(0, (currentStats.completed_tasks || 0) - 1);
                    }

                    // Add to new status count
                    if (newValue === 2) { // In Progress
                        currentStats.in_progress_tasks = (currentStats.in_progress_tasks || 0) + 1;
                    } else if (newValue === 4) { // Completed
                        currentStats.completed_tasks = (currentStats.completed_tasks || 0) + 1;
                    }
                });

                this.$store.dispatch('auth/set_user_stats', currentStats);
            }
            // Priority changes don't affect stats, so we don't need to handle them here
        },

        // Filter application - separate methods for personal and assigned tasks
        applyPersonalFilters(tasks) {
            // Create filters object with status from computed property and other filters from local data
            const filters = {
                ...this.personalFilters,
                status: this.personalStatusFilters
            };
            const filtered = this.applyCommonFilters(tasks, filters);
            return filtered;
        },

        applyAssignedFilters(tasks) {
            // Create filters object with status from computed property and other filters from local data
            const filters = {
                ...this.assignedFilters,
                status: this.assignedStatusFilters
            };
            const filtered = this.applyCommonFilters(tasks, filters);
            return filtered;
        },

        applyCreatedForOthersFilters(tasks) {
            // No status filter — base is already fixed to status_id = 8
            const filters = {
                ...this.createdForOthersFilters,
                status: null
            };
            return this.applyCommonFilters(tasks, filters);
        },

        applyCommonFilters(tasks, filters) {
            return tasks.filter(task => {
                // Status filter (primary filter - applied first)
                // If status filters are specified, only show tasks with those statuses
                if (filters.status && filters.status.length > 0) {
                    if (!filters.status.includes(task.status_id)) {
                        return false;
                    }
                }

                // Search filter (task name or description) - within selected statuses
                if (filters.search) {
                    const searchTerm = filters.search.toLowerCase();
                    const nameMatch = task.name && task.name.toLowerCase().includes(searchTerm);
                    const descMatch = task.description && task.description.toLowerCase().includes(searchTerm);
                    if (!nameMatch && !descMatch) return false;
                }

                // Company filter - within selected statuses
                if (filters.company !== null && task.company_id !== filters.company) {
                    return false;
                }

                // Priority filter - within selected statuses
                if (filters.priority !== null && task.priority_id !== filters.priority) {
                    return false;
                }

                // Assignee filter - within selected statuses
                if (filters.assignee != null) {
                    const hasAssignee = (task.assignees || []).some(a => a.user_id === filters.assignee);
                    if (!hasAssignee) return false;
                }

                // Due date filter - within selected statuses
                if (filters.dueDate) {
                    if (!task.due_date) return false;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0); // Start of today
                    const dueDate = new Date(task.due_date);
                    dueDate.setHours(0, 0, 0, 0); // Start of due date

                    switch (filters.dueDate) {
                        case 'overdue': {
                            // Check if task is overdue (before today, not including today) and not completed/cancelled
                            const isOverdue = dueDate < today;
                            const status = this.get_statuses.find(s => s.id === task.status_id);
                            const isCompleted = status && ['completed', 'cancelled'].includes(status.name.toLowerCase());
                            if (!isOverdue || isCompleted) return false;
                            break;
                        }
                        case 'today':
                            if (dueDate.toDateString() !== today.toDateString()) return false;
                            break;
                        case 'week': {
                            const weekFromNow = new Date(today);
                            weekFromNow.setDate(today.getDate() + 7);
                            if (!(dueDate >= today && dueDate <= weekFromNow)) return false;
                            break;
                        }
                        case 'month': {
                            const monthFromNow = new Date(today);
                            monthFromNow.setMonth(today.getMonth() + 1);
                            if (!(dueDate >= today && dueDate <= monthFromNow)) return false;
                            break;
                        }
                        default:
                            // 'All' or null - show all tasks
                            break;
                    }
                }

                return true;
            });
        },

        // Client-side sort applied on top of the already-filtered task array
        // for a tab. Purely presentational — doesn't touch task data.
        sortTasks(tasks, sortBy) {
            if (!sortBy || sortBy === 'none') return tasks;
            const sorted = [...tasks];
            switch (sortBy) {
                case 'due_date_asc':
                    sorted.sort((a, b) => {
                        if (!a.due_date && !b.due_date) return 0;
                        if (!a.due_date) return 1;
                        if (!b.due_date) return -1;
                        return new Date(a.due_date) - new Date(b.due_date);
                    });
                    break;
                case 'priority_desc':
                    // Lower priority_id = higher severity (1=High, 4=Low); tasks
                    // with no priority sort last regardless of direction.
                    sorted.sort((a, b) => (a.priority_id || Infinity) - (b.priority_id || Infinity));
                    break;
                case 'name_asc':
                    sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                    break;
                case 'created_desc':
                    sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
                    break;
            }
            return sorted;
        },

        clearPersonalFilters() {
            this.personalFilters = { search: '', company: null, priority: null, assignee: null, dueDate: null };
            this.personalGroupBy = 'status';
            this.personalSortBy = 'none';
            this.personalStatusFilters = this.getDefaultActiveStatusIds();
        },
        clearAssignedFilters() {
            this.assignedFilters = { search: '', company: null, priority: null, assignee: null, dueDate: null };
            this.assignedGroupBy = 'status';
            this.assignedSortBy = 'none';
            this.assignedStatusFilters = this.getDefaultActiveStatusIds();
        },
        clearCreatedForOthersFilters() {
            this.createdForOthersFilters = { search: '', company: null, priority: null, dueDate: null };
            this.createdForOthersGroupBy = 'company';
            this.createdForOthersSortBy = 'none';
        },
    }
}
</script>


<style lang="scss" scoped>
.voice-btn-recording {
    animation: recording-pulse 1.4s ease-in-out infinite;
}

@keyframes recording-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.5); }
    50%       { box-shadow: 0 0 0 8px rgba(244, 67, 54, 0); }
}

.voice-pulse {
    animation: icon-pulse 1.2s ease-in-out infinite;
}

@keyframes icon-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.4; }
}

.custom-menu {
    background-color: white !important;
    border-radius: 8px;
    padding: 10px;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
}

.tight-checkbox {
    padding: 0px !important;
    margin: 0px !important;
    min-height: 30px;
}

.v-checkbox {
    margin: 0px !important;
    padding: 2px 5px !important;
}

.custom-checkbox-text {
    font-size: 0.9rem;
    font-weight: 900;
    color: #333;
    text-transform: uppercase;
}

.task-view-toggle {
    padding: 16px;
    border-top: 1px solid #e0e0e0;
    background-color: #fafbfc;

    .v-btn-toggle {
        border-radius: 6px;
        overflow: hidden;

        .v-btn {
            font-weight: 500;
            text-transform: none;
            padding: 8px 16px;

            &:not(.v-btn--active) {
                background-color: white;
                color: #666;
            }

            &.v-btn--active {
                background-color: #1976d2;
                color: white;
            }

            .v-icon {
                margin-right: 4px;
            }
        }
    }
}

// Single-row work tabs (see activeWorkTab computed for the index mapping).
.view-mode-toggle {
    border-bottom: 1px solid #e0e0e0;
}

.view-mode-toggle .v-tab {
    font-size: 0.875rem;
    font-weight: 500;
    text-transform: none;
    letter-spacing: normal;
}

.gap-3 {
    gap: 12px;
}

.stats-strip {
    padding: 14px 20px;
    overflow-x: auto;
}

.stats-strip-items {
    display: flex;
    align-items: center;
    width: 100%;
}

.stats-strip-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.my-work-header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.stats-strip-divider {
    align-self: stretch;
    margin: 0 16px;
}

.quick-task-bar {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
}

.quick-task-queue {
    flex-basis: 100%;
}

.quick-task-bar-input {
    max-width: 420px;
    flex: 2 1 260px;
}

.quick-task-bar-company {
    max-width: 260px;
    flex: 1 1 180px;
}

.quick-task-toggle {
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    align-self:center;

    :deep(.v-btn) {
        min-width: 36px !important;
        padding: 0 8px !important;
    }
}

// Matches TaskFilterToolbar.vue's .filter-search — rounded, borderless
// solo/flat field style, reused here for the "View tasks for user" selector
// and the header quick-task fields.
.filter-search-style {
    border: 1px solid #e0e0e0;
    border-radius: 20px;

    :deep(.v-input__slot) {
        border-radius: 20px !important;
    }
}

.stats-strip-item {
    display: flex;
    align-items: center;
    gap: 12px;
    white-space: nowrap;
    flex: 1 1 0;
    justify-content: center;
}

.stats-strip-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    flex-shrink: 0;
}

.stats-strip-icon-blue {
    background-color: #e3f2fd;
}

.stats-strip-icon-orange {
    background-color: #fff3e0;
}

.stats-strip-icon-error {
    background-color: #ffebee;
}

.stats-strip-icon-success {
    background-color: #e8f5e9;
}

.stats-strip-text {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
}

.stats-strip-value {
    font-size: 1.25rem;
    font-weight: 700;
}

.stats-strip-label {
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.6);
}

.create-task-group {
    overflow: hidden;
    border-radius: 24px !important;
}

.icon-action-btn {
    box-shadow: none !important;
}

.stats-card {
    border-radius: 12px;
    transition: all 0.2s ease;
    min-height: 90px;
    position: relative;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
    }

    .text-xs {
        position: absolute;
        bottom: 8px;
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
    }
}

.gap-2 {
    gap: 8px;
}

.text-xs {
    font-size: 0.65rem !important;
    line-height: 1.1;
}

.task-controls {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e9ecef;
}

.gap-2 {
    gap: 8px !important;
}

@media (max-width: 768px) {
    .task-controls .d-flex {
        flex-direction: column;

        >* {
            width: 100% !important;
            max-width: none !important;
            min-width: auto !important;
        }
    }
}

/* Fade transition for smoother rendering */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}
</style>
